/**
 * AuthAnalytics — mounts Google Analytics for the auth funnel ONLY.
 *
 * Rendered inside each public auth page (login, register, forgot-password,
 * verify-email). Because it only exists on those pages, gtag.js is never loaded
 * behind the login and page_views only fire for auth routes. Consent Mode v2 is
 * bootstrapped on first mount and kept in sync with the analytics cookie
 * category. Safe no-op when GA is not configured (dev/staging have no id).
 *
 * It lives inside the auth pages (not a saas.config `wrappers[]` entry) on
 * purpose: wrappers sit ABOVE the RouterProvider, so `useCookieConsent` and
 * router context aren't reliably available there.
 */

import { useCookieConsent } from '@xhubio-saas/core-frontend/kernel'
import { useEffect } from 'react'
import { ensureGtagLoaded, setAnalyticsConsent, trackPageView } from './gtag'

export function AuthAnalytics({ path }: { path: string }) {
  const { isAllowed } = useCookieConsent()
  const analyticsAllowed = isAllowed('analytics')

  // Load gtag (Consent Mode v2, default denied) when an auth page mounts, then
  // fire this route's page_view. GA only ever loads inside the auth funnel.
  useEffect(() => {
    if (!ensureGtagLoaded()) return
    trackPageView(path)
  }, [path])

  // Reflect current analytics consent into Consent Mode (grant on accept,
  // revoke + clear _ga cookies on withdrawal).
  useEffect(() => {
    if (!ensureGtagLoaded()) return
    setAnalyticsConsent(analyticsAllowed)
  }, [analyticsAllowed])

  return null
}
